import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useLeads, usePipelineStats } from '@/hooks/useCRM'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Users, Mail, Building2, TrendingUp } from 'lucide-react'
import { useMemo } from 'react'

const STAGE_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  replied: 'Replied',
  meeting: 'Meeting',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
}

export function CRMStats() {
  const { leads, loading: leadsLoading } = useLeads({})
  const { stats, loading: statsLoading } = usePipelineStats()

  const chartData = useMemo(() => {
    const stages = [
      'new',
      'contacted',
      'replied',
      'meeting',
      'proposal',
      'negotiation',
      'won',
      'lost',
    ]
    return stages.map((stage) => ({
      stage: STAGE_LABELS[stage] ?? stage,
      count: Number(stats?.find((s) => s.pipeline_stage === stage)?.count ?? 0),
    }))
  }, [stats])

  const totalLeads = leads.length
  const contactedCount = leads.filter((l) => l.pipeline_stage !== 'new').length
  const uniqueCompanies = useMemo(
    () => new Set(leads.map((l) => l.company?.toLowerCase()).filter(Boolean)).size,
    [leads]
  )
  const today = new Date().toISOString().slice(0, 10)
  const contactedToday = useMemo(
    () =>
      leads.filter(
        (l) =>
          l.last_contacted_at && l.last_contacted_at.slice(0, 10) === today
      ).length,
    [leads, today]
  )

  const loading = leadsLoading || statsLoading

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total leads</p>
              <p className="text-2xl font-bold">{loading ? '—' : totalLeads.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-lg bg-green-500/10 p-2.5">
              <Mail className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contacted</p>
              <p className="text-2xl font-bold">
                {loading ? '—' : contactedCount.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-lg bg-cyan-500/10 p-2.5">
              <Building2 className="h-5 w-5 text-cyan-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Companies</p>
              <p className="text-2xl font-bold">
                {loading ? '—' : uniqueCompanies.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-lg bg-amber-500/10 p-2.5">
              <TrendingUp className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contacted today</p>
              <p className="text-2xl font-bold">{loading ? '—' : contactedToday}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pipeline by stage</CardTitle>
          <p className="text-sm text-muted-foreground">Lead count per pipeline stage</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="stage"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} width={28} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                    }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
